import { useEffect, useMemo, useState } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminReorderProducts,
  adminGetCategories, adminCreateCategory, adminDeleteCategory, adminUpload,
} from "@/lib/api";

const EMPTY = { title: "", description: "", category_id: "", price_usd: 0, price_php: 0, images: [], active: true, featured: false };

function SortableRow({ p, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`product-row-${p.id}`}
      className={`flex items-center gap-3 bg-white border border-[#1F3864]/10 rounded-xl px-3 py-2.5 ${isDragging ? "shadow-lg z-10 relative" : ""}`}
    >
      <button {...attributes} {...listeners} data-testid={`drag-${p.id}`} className="cursor-grab text-[#1F3864]/40 hover:text-[#1F3864] touch-none">
        <GripVertical size={18} />
      </button>
      {p.images?.[0] ? (
        <img src={p.images[0]} alt="" className="h-10 w-10 rounded-lg object-cover bg-[#F4F1EC]" />
      ) : (
        <div className="h-10 w-10 rounded-lg bg-[#F4F1EC] grid place-items-center text-[#1F3864]/30"><ImagePlus size={16} /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-[#1F3864] truncate">{p.title}</div>
        <div className="text-xs text-[#1F3864]/50">
          ${p.price_usd} / ₱{p.price_php} {!p.active && "· hidden"}
        </div>
      </div>
      <button data-testid={`edit-${p.id}`} onClick={() => onEdit(p)} className="p-2 text-[#1F3864]/60 hover:text-[#1F3864]"><Pencil size={16} /></button>
      <button data-testid={`delete-${p.id}`} onClick={() => onDelete(p)} className="p-2 text-[#FF8473]/70 hover:text-[#FF8473]"><Trash2 size={16} /></button>
    </div>
  );
}

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | product
  const [newCat, setNewCat] = useState("");
  const [uploading, setUploading] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = () => {
    adminGetProducts().then(setProducts).catch(() => toast.error("Failed to load products"));
    adminGetCategories().then(setCategories).catch(() => {});
  };
  useEffect(load, []);

  const grouped = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.id, []));
    map.set("__none", []);
    [...products].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).forEach((p) => {
      const key = p.category_id && map.has(p.category_id) ? p.category_id : "__none";
      map.get(key).push(p);
    });
    return map;
  }, [products, categories]);

  const onDragEnd = (catId) => (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const list = grouped.get(catId) || [];
    const oldIdx = list.findIndex((p) => p.id === active.id);
    const newIdx = list.findIndex((p) => p.id === over.id);
    const reordered = arrayMove(list, oldIdx, newIdx);
    const items = reordered.map((p, i) => ({ id: p.id, order: i, category_id: catId === "__none" ? null : catId }));
    setProducts((prev) => {
      const orderMap = new Map(items.map((i) => [i.id, i.order]));
      return prev.map((p) => (orderMap.has(p.id) ? { ...p, order: orderMap.get(p.id) } : p));
    });
    adminReorderProducts(items).catch(() => toast.error("Reorder failed"));
  };

  const save = async () => {
    try {
      const payload = { ...editing, category_id: editing.category_id || null };
      if (editing.id) await adminUpdateProduct(editing.id, payload);
      else await adminCreateProduct(payload);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await adminDeleteProduct(p.id).catch(() => toast.error("Delete failed"));
    toast.success("Product deleted");
    load();
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const r = await adminUpload(file);
      setEditing((e) => ({ ...e, images: [...(e.images || []), r.url] }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Products</h1>
          <p className="text-[#1F3864]/60">Drag to reorder. Group into categories like collections.</p>
        </div>
        <button
          data-testid="add-product-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 bg-[#1F3864] text-[#D7E85C] font-bold px-5 py-3 rounded-full hover:bg-[#16294B]"
        >
          <Plus size={16} /> Add product
        </button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Categories */}
        <div className="bg-white rounded-2xl border border-[#1F3864]/10 p-4 h-fit">
          <div className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50 mb-3">Categories</div>
          <div className="space-y-1 mb-4">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#F4F1EC] text-sm font-bold text-[#1F3864]">
                <span className="truncate">{c.name}</span>
                <button onClick={() => adminDeleteCategory(c.id).then(load)} className="text-[#FF8473]/60 hover:text-[#FF8473]"><X size={14} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="new-category-input"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              placeholder="New category"
              className="flex-1 min-w-0 text-sm border border-[#1F3864]/20 rounded-lg px-3 py-2 outline-none focus:border-[#1F3864]"
            />
            <button
              data-testid="add-category-btn"
              onClick={() => { if (newCat.trim()) { adminCreateCategory({ name: newCat.trim() }).then(() => { setNewCat(""); load(); }); } }}
              className="px-3 py-2 rounded-lg bg-[#D7E85C] text-[#1F3864] font-bold text-sm"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Product lists per category */}
        <div className="space-y-6">
          {[...categories.map((c) => ({ id: c.id, name: c.name })), { id: "__none", name: "Uncategorized" }].map((cat) => {
            const list = grouped.get(cat.id) || [];
            return (
              <div key={cat.id}>
                <div className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50 mb-2">
                  {cat.name} · {list.length}
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd(cat.id)}>
                  <SortableContext items={list.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {list.length === 0 && (
                        <div className="text-sm text-[#1F3864]/40 border border-dashed border-[#1F3864]/20 rounded-xl px-4 py-6 text-center">
                          No products here yet
                        </div>
                      )}
                      {list.map((p) => <SortableRow key={p.id} p={p} onEdit={setEditing} onDelete={remove} />)}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            );
          })}
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-[#1F3864]/60 backdrop-blur-sm p-4" onClick={() => setEditing(null)}>
          <div data-testid="product-editor" className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl text-[#1F3864]">{editing.id ? "Edit product" : "New product"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} className="text-[#1F3864]/50" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Title</label>
                <input data-testid="product-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Description</label>
                <textarea data-testid="product-desc-input" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={3} className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Price (USD)</label>
                  <input data-testid="product-price-usd" type="number" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: +e.target.value })}
                    className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864]" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Price (PHP)</label>
                  <input data-testid="product-price-php" type="number" value={editing.price_php} onChange={(e) => setEditing({ ...editing, price_php: +e.target.value })}
                    className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Category</label>
                <select data-testid="product-category-select" value={editing.category_id || ""} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}
                  className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F3864] bg-white">
                  <option value="">Uncategorized</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Images</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(editing.images || []).map((url, i) => (
                    <div key={i} className="relative">
                      <img src={url} alt="" className="h-16 w-16 rounded-lg object-cover bg-[#F4F1EC]" />
                      <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#FF8473] text-white grid place-items-center"><X size={12} /></button>
                    </div>
                  ))}
                  <label data-testid="product-upload-btn" className="h-16 w-16 rounded-lg border-2 border-dashed border-[#1F3864]/20 grid place-items-center cursor-pointer text-[#1F3864]/40 hover:border-[#1F3864]">
                    {uploading ? <span className="text-xs">…</span> : <ImagePlus size={20} />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                  </label>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-bold text-[#1F3864]">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-[#1F3864]" />
                Visible on store
              </label>
              <button data-testid="product-save-btn" onClick={save}
                className="w-full bg-[#1F3864] text-[#D7E85C] font-bold py-3 rounded-full hover:bg-[#16294B]">
                Save product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
